import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Label,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import ASCButton from "../Button";

interface DeleteSummary {
  activeOnPortal: boolean;
  casesCount: number;
  commissionStatus: string;
  notesCount: number;
  documentsCount: number;
  hasPartner?: boolean;
  partnerName?: string | null;
  blocked: boolean;
  blockedMessage?: string;
}

interface DeleteClientSummaryPopupProps {
  open: boolean;
  whoText: string;
  summary: DeleteSummary | null;
  summaryLoading: boolean;
  deleteApiProcessing: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

function DeleteClientSummaryPopup(props: DeleteClientSummaryPopupProps) {
  const noButtonRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState("");

  // Default focus must land on "No"/"OK" — a keyboard user pressing Enter
  // without deliberately tabbing to a button must not trigger the delete.
  useEffect(() => {
    if (props.open && !props.summaryLoading) {
      noButtonRef.current?.focus();
    }
  }, [props.open, props.summaryLoading]);

  // Reset the reason each time the dialog is (re)opened for a new client.
  useEffect(() => {
    if (props.open) {
      setReason("");
    }
  }, [props.open]);

  const blocked = !!props.summary?.blocked;

  return (
    <Dialog open={props.open}>
      <DialogSurface style={{ maxWidth: 540 }}>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: "50%",
                  background: "#fbeaea",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                }}
              >
                {blocked ? "⚠" : "🗑"}
              </div>
              <span>{blocked ? "Cannot Delete Client" : "Delete Client"}</span>
            </div>
          </DialogTitle>
          <DialogContent>
            {props.summaryLoading ? (
              <Spinner size="small" label="Loading summary..." />
            ) : blocked ? (
              <p style={{ fontSize: 13.5, color: "#5a6a85", lineHeight: 1.5 }}>
                {props.summary?.blockedMessage ||
                  "There are active commissions associated with this client so it cannot be deleted."}
              </p>
            ) : (
              <>
                <p
                  style={{
                    fontSize: 13.5,
                    color: "#5a6a85",
                    lineHeight: 1.5,
                    marginTop: 0,
                    marginBottom: 12,
                  }}
                >
                  Are you sure you want to delete {props.whoText}?
                </p>
                <div
                  style={{
                    background: "#f7f8fa",
                    border: "1px solid #e8ecf3",
                    borderRadius: 6,
                    padding: "10px 14px",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: "#1a202c",
                      marginBottom: 6,
                    }}
                  >
                    The following will be affected when you delete{" "}
                    {props.whoText}:
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 16,
                      fontSize: 12.5,
                      color: "#5a6a85",
                      lineHeight: 1.65,
                    }}
                  >
                    <li>
                      Client Portal:{" "}
                      {props.summary?.activeOnPortal
                        ? "Active (will be deactivated)"
                        : "Inactive"}
                    </li>
                    <li>Cases associated: {props.summary?.casesCount ?? 0}</li>
                    <li>
                      Commission: {props.summary?.commissionStatus ?? "None"}
                    </li>
                    {props.summary?.hasPartner && (
                      <li>
                        Partner: {props.summary?.partnerName || "associated partner"} (will also be deleted)
                      </li>
                    )}
                    <li>Notes: {props.summary?.notesCount ?? 0}</li>
                    <li>Documents: {props.summary?.documentsCount ?? 0}</li>
                  </ul>
                </div>
                <Label
                  htmlFor="delete-client-reason"
                  style={{
                    fontWeight: 600,
                    fontSize: 12.5,
                    color: "#1a202c",
                    display: "block",
                    marginBottom: 5,
                  }}
                >
                  Reason (optional)
                </Label>
                <Textarea
                  id="delete-client-reason"
                  resize="vertical"
                  value={reason}
                  onChange={(_, data) => setReason(data.value)}
                  textarea={{ style: { minHeight: 56 } }}
                  style={{ width: "100%" }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions
            style={{
              gridColumnStart: 1,
              gridColumnEnd: 4,
              justifySelf: "center",
              justifyContent: "center",
            }}
          >
            <ASCButton
              ref={noButtonRef}
              shape="rounded"
              className="asc-button-primary"
              appearance="primary"
              onClick={props.onCancel}
              disabled={!blocked && props.deleteApiProcessing}
              label={blocked ? "OK" : "No"}
            />
            {!blocked && (
              <ASCButton
                shape="rounded"
                appearance="secondary"
                onClick={() => props.onConfirm(reason)}
                disabled={props.summaryLoading || props.deleteApiProcessing}
                icon={props.deleteApiProcessing ? <Spinner size="tiny" /> : null}
                label={props.deleteApiProcessing ? "Processing..." : "Yes, Delete"}
                style={{ color: "#c0392b", borderColor: "#e3b3b3" }}
              />
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default DeleteClientSummaryPopup;
